import React, { useState, useEffect } from 'react';
import config from '../config';

const TIMEZONE_STATE_MAP = {
  "America/Los_Angeles": ["CA", "WA", "OR", "NV"],
  "America/Denver": ["AZ", "CO", "ID", "MT", "NM", "UT", "WY"],
  "America/Chicago": ["AL", "AR", "IA", "IL", "KS", "LA", "MN", "MO", "MS", "ND", "NE", "OK", "SD", "TX", "WI"],
  "America/New_York": ["CT", "DC", "DE", "FL", "GA", "IN", "KY", "MA", "MD", "ME", "MI", "NC", "NH", "NJ", "NY", "OH", "PA", "RI", "SC", "TN", "VA", "VT", "WV"]
};

// Global course ordering used across UI. Partial match (case-insensitive) is used
// so suffixes like "2026" won't break the ordering.
const COURSE_ORDER = [
  "Patient Introduction SMA",
  "Opioid Expectations and Agreements",
  "Opioids Today: Facts, Risks, and Realities",
  "Understanding the Benefits of Opioid Therapy",
  "Recognizing Opioid Risks",
  "Working Together for Safer, Compliant Care",
  "Understanding Barriers to Opioids",
  "Exploring Treatment Alternatives",
  "Medication Options Beyond Opioids",
  "Training the Mind to Manage Pain",
  "Physical Movement for Pain Management",
  "Complementary and Alternative Pain Therapies",
  "Opioid Tapering: Managing Change Safely",
];

export const sortCourses = (list = []) => {
  const orderKeys = COURSE_ORDER.map((s) => s.toLowerCase());
  const getNum = (name) => {
    const m = typeof name === "string" ? name.match(/(\d+)/) : null;
    return m ? parseInt(m[1], 10) : Number.POSITIVE_INFINITY;
  };

  return [...list].sort((a, b) => {
    const sa = String(a || '').toLowerCase();
    const sb = String(b || '').toLowerCase();

    const ia = orderKeys.findIndex((k) => sa.includes(k));
    const ib = orderKeys.findIndex((k) => sb.includes(k));

    // If either item matches the ordered list, prioritize by that ordering.
    if (ia !== -1 || ib !== -1) {
      if (ia === -1) return 1; // a not in list, b is -> b first
      if (ib === -1) return -1; // b not in list, a is -> a first
      if (ia !== ib) return ia - ib;
      // both match same ordered key (rare) — fallback to string compare
      return sa.localeCompare(sb);
    }

    // Neither matched the predefined order — fall back to previous numeric/name sort
    const na = getNum(sa);
    const nb = getNum(sb);
    if (na !== nb) return na - nb;
    return sa.localeCompare(sb);
  });
};

const resolveTimezoneByState = (state) => {
  if (!state) return null;
  // First match wins when a state appears in multiple timezone lists.
  for (const [timezone, states] of Object.entries(TIMEZONE_STATE_MAP)) {
    if (states.includes(state)) return timezone;
  }
  return null;
};

// Generic SelectDropdown component
export const SelectDropdown = ({ value, onChange, options, label, className = "field-select", placeholder, isMandatory = false, changed = false }) => (
  <div className="object-field primitive-field">
    {label && <label className="field-name">{label}{isMandatory ? <span className="mandatory-indicator">*</span> : ''}:</label>}
    <select value={value || ''} onChange={e => onChange(e.target.value)} className={`${className} ${changed ? 'changed-field' : ''}`}>
      <option value="">{placeholder || `Select ${label?.toLowerCase() || 'option'}`}</option>
      {options.map(option => <option key={option} value={option}>{option}</option>)}
    </select>
  </div>
);

// Generic EditableListDropdown component for state, license, credentialing
export const EditableListDropdown = ({ value = [], onChange, options, label, maxLength, transformValue = v => v, isMandatory = false, itemDisplay = "input" }) => {
  const [newItem, setNewItem] = useState(options.find(opt => !value.includes(opt)) || '');

  const handleRemove = (itemToRemove) => onChange(value.filter(item => item !== itemToRemove));

  const handleItemChange = (index, newValue) => {
    const newValues = [...value];
    newValues[index] = transformValue(newValue);
    onChange(newValues);
  };

  const handleAdd = () => {
    if (newItem && !value.includes(newItem)) {
      onChange([...value, newItem]);
      const next = options.find(opt => !value.includes(opt) && opt !== newItem);
      setNewItem(next || '');
    }
  };

  const renderExistingItem = (item, idx) => {
    if (itemDisplay === "span") {
      return (
        <span key={item} className="license-item mr5 flex-center">
          {item}
          <button type="button" onClick={() => handleRemove(item)} className="btn-remove ml2" aria-label={`Remove ${item}`}>
            ×
          </button>
        </span>
      );
    } else {
      return (
        <div key={item} className="flex-center-mb6">
          <input
            type="text"
            value={item}
            onChange={e => handleItemChange(idx, e.target.value)}
            maxLength={maxLength}
            className="license-input"
          />
          <button type="button" onClick={() => handleRemove(item)} className="btn-remove" aria-label={`Remove ${item}`}>
            Remove
          </button>
        </div>
      );
    }
  };

  return (
    <div className="object-field primitive-field mb16">
      <label className="field-name">{label}{isMandatory ? <span className="mandatory-indicator">*</span> : ''}:</label>
      <div className="field-value">
        <div className="existing-items flex-wrap mb10">
          {value.map((item, idx) => renderExistingItem(item, idx))}
        </div>
        <div className="add-section flex-end mt20">
          <select value={newItem} onChange={e => setNewItem(e.target.value)} className="add-select mr10" aria-label={`Select ${label.toLowerCase()} to add`}>
            <option value="">Select to add</option>
            {options.filter(opt => !value.includes(opt)).map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
          <button type="button" onClick={handleAdd} className="btn-add" disabled={!newItem} aria-label={`Add ${label.toLowerCase()}`}>
            Add
          </button>
        </div>
      </div>
    </div>
  );
};



// Specialized dropdown components using EditableListDropdown
export const StateDropdown = ({ value, onChange, isMandatory = false, path = [], changedFields = new Set() }) => {
  const handleStateChange = (newState) => {
    const timezone = resolveTimezoneByState(newState);
    onChange(newState, timezone);
  };

  return (
    <SelectDropdown
      value={value}
      onChange={handleStateChange}
      options={["AK", "AL", "AR", "AZ", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "IA", "ID", "IL", "IN", "KS", "KY", "LA", "MA", "MD", "ME", "MI", "MN", "MO", "MS", "MT", "NC", "ND", "NE", "NH", "NJ", "NM", "NV", "NY", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VA", "VT", "WA", "WI", "WV", "WY"]}
      label="State"
      isMandatory={isMandatory}
      changed={changedFields.has(path.join('.'))}
    />
  );
};

export const LicenseDropdown = ({ value = [], onChange, isMandatory = false, path = [], changedFields = new Set() }) => {
  // Display existing licenses without the box, but keep add/remove functionality
  const [newItem, setNewItem] = useState('');

  const handleRemove = (itemToRemove) => onChange(value.filter(item => item !== itemToRemove));

  const handleAdd = () => {
    if (newItem && !value.includes(newItem)) {
      onChange([...value, newItem]);
      setNewItem('');
    }
  };

  const stateOptions = ["AK", "AL", "AR", "AZ", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "IA", "ID", "IL", "IN", "KS", "KY", "LA", "MA", "MD", "ME", "MI", "MN", "MO", "MS", "MT", "NC", "ND", "NE", "NH", "NJ", "NM", "NV", "NY", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VA", "VT", "WA", "WI", "WV", "WY"];

  return (
    <div className={`object-field custom-layout primitive-field mb16 ${changedFields.has(path.join('.')) ? 'changed-field' : ''}`}>
      <label className="field-name">License{isMandatory ? <span className="mandatory-indicator">*</span> : ''}:</label>
      <div className="license-value-area license-value-column">
        <div className="existing-items-license flex-wrap">
          {value.map((item, idx) => (
            <span key={item} className={`license-item mr5 flex-center ${changedFields.has(`${path.join('.')}.${idx}`) ? 'changed-field' : ''}`}>
              <span className="license-text">{item}</span>
              <button type="button" onClick={() => handleRemove(item)} className="btn-remove ml2" aria-label={`Remove ${item}`}>
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="add-section flex-start" style={{ justifyContent: 'flex-start', alignItems: 'center', gap: '10px' }}>
          <select value={newItem} onChange={e => setNewItem(e.target.value)} className="add-select mr10" aria-label="Select license to add">
            <option value="">Select license</option>
            {stateOptions.filter(opt => !value.includes(opt)).map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          <button type="button" onClick={handleAdd} className="btn-add" disabled={!newItem} aria-label="Add license">
            Add
          </button>
        </div>
      </div>
    </div>
  );
};

export const CredentialingDropdown = ({ value = [], onChange, data, isMandatory = false, path = [], changedFields = new Set() }) => {
  // Display existing credentials without the box, but keep add/remove functionality
  const practiceOptions = [...new Set((data?.practices || []).map(p => p.name).filter(name => name && name.trim()))];
  const [newItem, setNewItem] = useState('');

  const handleRemove = (itemToRemove) => onChange(value.filter(item => item !== itemToRemove));

  const handleAdd = () => {
    if (newItem && !value.includes(newItem)) {
      onChange([...value, newItem]);
      setNewItem('');
    }
  };

  return (
    <div className={`object-field custom-layout primitive-field mb16 ${changedFields.has(path.join('.')) ? 'changed-field' : ''}`}>
      <label className="field-name">Credentialing:</label>
      <div className="license-value-area license-value-column">
        <div className="existing-items flex-wrap">
          {value.map((item, idx) => (
            <span key={item} className={`license-item mr5 flex-center ${changedFields.has(`${path.join('.')}.${idx}`) ? 'changed-field' : ''}`}>
              <span className="license-text">{item}</span>
              <button type="button" onClick={() => handleRemove(item)} className="btn-remove ml2" aria-label={`Remove ${item}`}>
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="add-section-credential flex-start">
          <select value={newItem} onChange={e => setNewItem(e.target.value)} className="add-select mr10" aria-label="Select credentialing to add">
            <option value="">Select credentialing</option>
            {practiceOptions.filter(opt => !value.includes(opt)).map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          <button type="button" onClick={handleAdd} className="btn-add" disabled={!newItem} aria-label="Add credentialing">
            Add
          </button>
        </div>
      </div>
    </div>
  );
};

export const TimezoneDropdown = ({ value, onChange, isMandatory = false, path = [], changedFields = new Set() }) => (
  <SelectDropdown
    value={value}
    onChange={onChange}
    options={["America/Los_Angeles", "America/Denver", "America/Chicago", "America/New_York"]}
    label="Timezone"
    isMandatory={isMandatory}
    changed={changedFields.has(path.join('.'))}
  />
);

// State for appointment types from API
let appointmentTypesCache = [];
let appointmentTypesLoaded = false;

// Function to fetch appointment types from mock API
export const fetchAppointmentTypesFromMock = async () => {
  try {
    const timestamp = new Date().getTime();
    const response = await fetch(`${config.SMA_SCHEDULER_SERVICE_BASE_URL}/appointment-types?_t=${timestamp}`, {
      method: 'GET',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    // Extract names from mock data
    const names = data.map(item => item.name.trim());
    return sortCourses(names);
  } catch (error) {
    console.error('Error fetching appointment types from mock:', error.message);
    return [];
  }
};

// Function to fetch appointment types from FastAPI backend (already filtered)
const fetchAppointmentTypes = async (useStaticCourses = false) => {
  if (useStaticCourses) {
    // When useStaticCourses is true, fetch from mock API (no caching for mock)
    return await fetchAppointmentTypesFromMock();
  }

  if (appointmentTypesLoaded) {
    return appointmentTypesCache;
  }

  try {
    const token = sessionStorage.getItem('token');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${config.SMA_SCHEDULER_SERVICE_BASE_URL}/appointment-types`, {
      cache: 'no-cache',
      headers
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    // Data is already filtered by backend, just extract names
    const filtered_names = data.map(item => item.name.trim());
    appointmentTypesCache = sortCourses(filtered_names);
    appointmentTypesLoaded = true;
    return appointmentTypesCache;
  } catch (error) {
    console.error('Error fetching appointment types:', error.message);
    return [];
  }
};


// Get courses options from appointment types
export const getCoursesOptions = async (useStaticCourses = false) => {
  const appointmentTypes = await fetchAppointmentTypes(useStaticCourses);
  return sortCourses(appointmentTypes);
};


export const CoursesDropdown = ({ value = [], onChange, isMandatory = false, useStaticCourses, coursesOptions: propCoursesOptions }) => {
  const [coursesOptions, setCoursesOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState('');

  // Fetch appointment types on component mount and when useStaticCourses changes
  useEffect(() => {
    if (propCoursesOptions && propCoursesOptions.length > 0) {
      setCoursesOptions(sortCourses(propCoursesOptions));
      setLoading(false);
      return;
    }

    setLoading(true);
    const loadCourses = async () => {
      try {
        const options = await getCoursesOptions(useStaticCourses);
        setCoursesOptions(sortCourses(options));
      } catch (error) {
        console.error('Error loading courses:', error.message);
      } finally {
        setLoading(false);
      }
    };

    loadCourses();
  }, [useStaticCourses, propCoursesOptions]);

  // value is array of course names, display directly
  const displayValue = value;

  const handleAdd = () => {
    if (newItem && !displayValue.includes(newItem)) {
      // Store the full course name directly and sort by chronological order
      const newCourses = [...value, newItem].sort((a, b) => {
        // Extract numbers from course names for sorting
        const numA = parseInt(a.match(/^(\d+)/)?.[1] || '0');
        const numB = parseInt(b.match(/^(\d+)/)?.[1] || '0');
        return numA - numB;
      });
      onChange(newCourses);
      setNewItem('');
    }
  };

  if (loading) {
    return (
      <div className="flex-center">
        <span>Loading courses...</span>
      </div>
    );
  }

  return (
    <div className="flex-center">
      <select value={newItem} onChange={e => setNewItem(e.target.value)} className="add-select-course" aria-label="Select course to add">
        <option value="">Select course</option>
        {coursesOptions.filter(opt => !displayValue.includes(opt)).map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
      <button type="button" onClick={handleAdd} className="btn-add" disabled={!newItem} aria-label="Add course">
        Add
      </button>
    </div>
  );
};
